/* eslint-disable react/prop-types */
/* eslint-disable no-control-regex */

import React, { useState } from 'react';
import styled from 'styled-components';

import { object as YupObject } from 'yup';
import { Formik } from 'formik';

import Input from '@ntuctech/devex-tangram/Input';
import Button from '@ntuctech/devex-tangram/Button';
import Typography from '@ntuctech/devex-tangram/Typography';
import Link from '@ntuctech/devex-tangram/Link';
import Checkbox from '@ntuctech/devex-tangram/Checkbox';
import Box from '@ntuctech/devex-tangram/Box';

import InputValidator from '../../services/input-validator';
import GA from '../../services/google-analytics-tags';

import { useAuth0 } from '../Auth0Provider';
import Error from '../theme/Error';
import Logger from '../../services/logger';
import sentry from '../../services/sentry';

import FormElement from '../theme/FormElement';
import FormActions from '../theme/FormActions';
import FormNote from '../theme/FormNote';

const { captureException } = sentry();

const CheckboxSC = styled(Checkbox)`
  height: auto !important;
  border-top: 0.0625rem solid #d8d8d8;
  padding-top: 1.5rem;

  & ._ndsCheck__styled {
    min-width: 1.25rem;
    margin-left: 0 !important;
    margin-right: 0.375rem !important;
  }

  & p {
    color: #4d4d4d !important;
  }
`;

function FormContent(props) {
  const {
    values,
    errors,
    handleChange,
    handleBlur,
    touched,
    isValid,
    dirty,
    isSubmitting,
    submitForm,
  } = props;

  const { email, mobile, password, marketingConsentPreference } = values;
  const { errors: formErrors = {} } = errors;
  const { password: passwordErrors = '' } = formErrors;
  const [optIn, setOptIn] = useState(marketingConsentPreference);

  const isPasswordValidFor = (criteria) => {
    return (password || touched.password) && !passwordErrors.includes(criteria);
  };
  const passWordHelpText = {
    title: 'Password must have:',
    validations: [
      {
        isValid: isPasswordValidFor('min8'),
        message: 'At least 8 characters in length',
      },
      {
        isValid: isPasswordValidFor('min1Number'),
        message: 'At least 1 number',
      },
      {
        isValid: isPasswordValidFor('min1LowerUpperCaseLetter'),
        message: 'At least 1 lower or upper case letter',
      },
    ],
  };

  const showErrorFor = (name) => {
    return touched[name] && Boolean(formErrors[name]);
  };
  const showEmailError = showErrorFor('email');
  const showMobileError = showErrorFor('mobile');
  const showPasswordError = showErrorFor('password');

  const isSubmitButtonDisabled = !dirty || !isValid || isSubmitting;

  const handleFocus = React.useCallback((event) => {
    GA.track('Sign-up', `Sign-up Interacted`, event.target.name);
  }, []);

  const handleKeyPress = React.useCallback(
    (event) => {
      if (event.key === 'Enter' && !isSubmitButtonDisabled) {
        event.target.blur();
        submitForm();
      }
    },
    [submitForm, isSubmitButtonDisabled]
  );

  const toggleOptIn = React.useCallback(
    (e) => {
      if (optIn) {
        setOptIn(false);
      } else {
        setOptIn(true);
      }
      handleChange(e);
    },
    [handleChange, optIn, setOptIn]
  );

  return (
    <>
      <FormElement>
        <Input
          type="email"
          name="email"
          label="Email address"
          helpText={showEmailError ? formErrors.email : ''}
          error={showEmailError}
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
          value={email}
          required
          fullWidth
          autoComplete="username"
          onKeyPress={handleKeyPress}
          data-testid="signUpEmail"
        />
      </FormElement>
      <FormElement>
        <Input
          type="tel"
          className="SSOPage__input-number-reset"
          name="mobile"
          label="Mobile number"
          helpText={showMobileError ? formErrors.mobile : ''}
          error={showMobileError}
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
          value={mobile}
          maxLength={8}
          required
          fullWidth
          autoComplete="tel-national"
          onKeyPress={handleKeyPress}
          data-testid="signUpMobileNumber"
        />
      </FormElement>
      <FormElement>
        <Input
          type="password"
          name="password"
          label="Password"
          helpText={passWordHelpText}
          error={showPasswordError}
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
          value={password}
          required
          fullWidth
          autoComplete="new-password"
          onKeyPress={handleKeyPress}
          data-testid="signUpPassword"
        />
      </FormElement>
      <FormElement>
        <CheckboxSC
          data-testid="signUpOptIn"
          name="marketingConsentPreference"
          label="Yes, please send me my exclusive welcome voucher, store promos and personalised offers on email and sms."
          checked={optIn}
          onChange={toggleOptIn}
        />
      </FormElement>
      <Box mt={-2} mb={-2}>
        <FormActions>
          <Button
            variant="contained"
            fullWidth
            compact
            disabled={isSubmitButtonDisabled}
            onClick={submitForm}
            data-testid="signUp"
          >
            Create Account
          </Button>
        </FormActions>
      </Box>
      <FormNote>
        <Typography variant="body3">
          By creating an account with us, you acknowledge you have read and agreed to our{' '}
          <Link
            href="https://help.fairprice.com.sg/hc/en-us/articles/360025882372-Terms-Conditions"
            target="_blank"
          >
            Terms and Conditions
          </Link>{' '}
          and{' '}
          <Link
            href="https://help.fairprice.com.sg/hc/en-us/articles/360025882432-Privacy-Policy"
            target="_blank"
          >
            Privacy Policy
          </Link>{' '}
        </Typography>
      </FormNote>
    </>
  );
}

export default function SignUpForm() {
  const Auth0Instance = useAuth0();

  const [error, setError] = useState(null);

  const initialValues = {
    email: '',
    mobile: '',
    password: '',
    marketingConsentPreference: true,
  };

  const validate = (values) => {
    const schema = YupObject().shape({
      email: InputValidator.emailValidation({
        emptyEmailMessage: 'Email address is required.',
        invalidEmailMessage: 'Email address is not valid.',
        maxLengthMessage: 'Your email address should be within 64 characters.',
      }),
      mobile: InputValidator.contactNumberValidation({
        invalidContactNumberMessage:
          'Please enter a valid 8-digit Singapore phone number starting with 8 or 9.',
      }),
      password: InputValidator.newPasswordValidation({
        minLengthMessage: 'min8',
        minOneNumberMessage: 'min1Number',
        minOneLetterMessage: 'min1LowerUpperCaseLetter',
      }).label('Password'),
    });

    let errors = {};

    try {
      schema.validateSync(values, { abortEarly: false });
    } catch (e) {
      e.errors = { password: '' };
      e.inner.forEach((elmError) => {
        if (elmError.path === 'password') {
          e.errors.password += elmError.errors[0];
        } else {
          [e.errors[elmError.path]] = elmError.errors;
        }
      });
      errors = e;
    }

    return errors;
  };

  const submit2Auth0 = (values, actions) => {
    GA.track('Sign-up', 'Sign-up Initiated');
    Auth0Instance.signUp(
      {
        ...values,
      },
      (err) => {
        actions.setSubmitting(false);
        setError(err);
        if (err) {
          GA.track('Sign-up', 'Sign-up Failure');
          Logger.error(err);
          return;
        }
        GA.track('Sign-up', 'Sign-up Success');
      }
    );
    return false;
  };

  const onSubmit = (values, actions) => {
    if (
      window.PasswordCredential &&
      window.PublicKeyCredential &&
      typeof PublicKeyCredential !== 'undefined'
    ) {
      const { email, password } = values;
      // eslint-disable-next-line no-undef
      const c = new PasswordCredential({ id: email, password });
      navigator.credentials
        .store(c)
        .catch((err) => {
          captureException(err);
          Logger.error(err);
        })
        .finally(() => {
          submit2Auth0(values, actions);
        });
    } else {
      submit2Auth0(values, actions);
    }
  };

  return (
    <>
      <Error error={error} />
      <Formik initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
        {(formikProps) => <FormContent {...formikProps} />}
      </Formik>
    </>
  );
}
